/**
 * O editor de atendimento sobre o PostgREST — trilha T5a.
 *
 * Pausar, retomar, reabrir e o rascunho da ficha passam pelas RPCs da `0083`:
 * são transições com regra (só consulta aberta pausa, só a encerrada hoje
 * reabre), e regra de transição mora no banco, não na tela.
 */

import { supabase } from '@/supabase';
import { atual } from '@/session/sessao';
import type { AtendimentoEmAndamento, PausaDoAtendimento, RascunhoDaFicha } from './atendimentoEmCurso';
import type { PortaDeDados } from './porta';
import type { RespostaDaFicha } from './tipos';

type MetodosDoAtendimento = Pick<
    PortaDeDados,
    | 'pausasDoAtendimento' | 'pausarAtendimento' | 'retomarAtendimento' | 'reabrirAtendimento'
    | 'atendimentoEmAndamento' | 'rascunhoDaFicha' | 'salvarRascunhoDaFicha' | 'descartarRascunhoDaFicha'
>;

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

function clinicaId(): string {
    const id = atual()?.clinicaId;
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

function erro(mensagem: string, e: { message: string; code?: string } | null): void {
    if (!e) return;
    if (e.code === '42501') throw new Error('Esta ação exige a permissão de atendimento.');
    /* A regra de transição já fala com quem está na tela: a frase é do banco. */
    if (e.code === 'P0001' || e.code === '22023') throw new Error(e.message);
    throw new Error(`${mensagem}: ${e.message}`);
}

/** `at-<agendamento>` é consulta que ainda não virou atendimento: sem pausa nem rascunho. */
function aindaSemAtendimento(id: string): boolean {
    return id.startsWith('at-');
}

export const atendimentoNoSupabase: MetodosDoAtendimento = {
    async pausasDoAtendimento(atendimentoId: string): Promise<PausaDoAtendimento[]> {
        if (aindaSemAtendimento(atendimentoId)) return [];
        const { data, error } = await cliente()
            .from('pausas_do_atendimento')
            .select('id, de, ate')
            .eq('atendimento_id', atendimentoId)
            .order('de');
        erro('Falha ao ler as pausas da consulta', error);
        return ((data ?? []) as unknown as { id: string; de: string; ate: string | null }[])
            .map((p) => ({ id: p.id, de: p.de, ...(p.ate ? { ate: p.ate } : {}) }));
    },

    async pausarAtendimento(atendimentoId: string): Promise<void> {
        const { error } = await cliente().rpc('pausar_atendimento', { p_atendimento: atendimentoId });
        erro('Falha ao pausar a consulta', error);
    },

    async retomarAtendimento(atendimentoId: string): Promise<void> {
        const { error } = await cliente().rpc('retomar_atendimento', { p_atendimento: atendimentoId });
        erro('Falha ao retomar a consulta', error);
    },

    async reabrirAtendimento(atendimentoId: string, motivo: string): Promise<void> {
        const { error } = await cliente().rpc('reabrir_atendimento', { p_atendimento: atendimentoId, p_motivo: motivo });
        erro('Falha ao reabrir a consulta', error);
    },

    async atendimentoEmAndamento(): Promise<AtendimentoEmAndamento | null> {
        const email = atual()?.email;
        if (!email) return null;
        const clinica = clinicaId();

        const { data: membro, error: erroDoMembro } = await cliente()
            .from('membros').select('id').eq('clinica_id', clinica).eq('email', email).maybeSingle();
        erro('Falha ao ler o membro da sessão', erroDoMembro);
        const membroId = (membro as unknown as { id: string } | null)?.id;
        if (!membroId) return null;

        /* A pílula é da pessoa que atende: a consulta aberta de outro
           profissional não é dela para retomar. */
        const { data, error } = await cliente()
            .from('atendimentos')
            .select('id, iniciado_em, contatos(nome), agendamentos!inner(profissional_id)')
            .eq('clinica_id', clinica)
            .eq('situacao', 'em-atendimento')
            .eq('agendamentos.profissional_id', membroId)
            .order('iniciado_em', { ascending: false })
            .limit(1);
        erro('Falha ao ler a consulta em andamento', error);

        const linha = ((data ?? []) as unknown as {
            id: string;
            iniciado_em: string | null;
            contatos: { nome: string } | { nome: string }[] | null;
        }[])[0];
        if (!linha?.iniciado_em) return null;
        const contato = Array.isArray(linha.contatos) ? linha.contatos[0] : linha.contatos;

        const { data: abertas, error: erroDasPausas } = await cliente()
            .from('pausas_do_atendimento').select('id').eq('atendimento_id', linha.id).is('ate', null).limit(1);
        erro('Falha ao ler as pausas da consulta', erroDasPausas);

        return { id: linha.id, paciente: contato?.nome ?? 'Paciente', iniciadoEm: linha.iniciado_em, pausado: (abertas ?? []).length > 0 };
    },

    async rascunhoDaFicha(atendimentoId: string, modeloId: string): Promise<RascunhoDaFicha | null> {
        if (aindaSemAtendimento(atendimentoId)) return null;
        const { data, error } = await cliente()
            .from('rascunhos_de_ficha')
            .select('respostas, atualizado_em')
            .eq('atendimento_id', atendimentoId)
            .eq('modelo_id', modeloId)
            .maybeSingle();
        erro('Falha ao ler o rascunho da ficha', error);
        const linha = data as unknown as { respostas: Record<string, RespostaDaFicha>; atualizado_em: string } | null;
        return linha ? { respostas: linha.respostas, atualizadoEm: linha.atualizado_em } : null;
    },

    async salvarRascunhoDaFicha(atendimentoId: string, modeloId: string, respostas: Record<string, RespostaDaFicha>): Promise<RascunhoDaFicha> {
        if (aindaSemAtendimento(atendimentoId)) throw new Error('Abra a consulta antes de escrever a ficha.');
        const { data, error } = await cliente().rpc('salvar_rascunho_da_ficha', {
            p_atendimento: atendimentoId, p_modelo: modeloId, p_respostas: respostas,
        });
        erro('Falha ao salvar o rascunho', error);
        return { respostas, atualizadoEm: String(data) };
    },

    async descartarRascunhoDaFicha(atendimentoId: string, modeloId: string): Promise<void> {
        if (aindaSemAtendimento(atendimentoId)) return;
        const { error } = await cliente()
            .from('rascunhos_de_ficha').delete().eq('atendimento_id', atendimentoId).eq('modelo_id', modeloId);
        erro('Falha ao descartar o rascunho', error);
    },
};
